import React, { FC } from 'react'
import { ControllerRenderProps } from 'react-hook-form'
import compact from 'lodash/compact'
import AutocompleteBase from 'transfix-ui/components/AutocompleteBase'
import { TInputChangeHandler } from 'transfix-ui/components/AutocompleteBase/hooks'
import { useAutocompleteQuery } from 'transfix-ui/components/AutocompleteBase/hooks/useAutocompleteQuery'
import { IAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase/types'
import { createStyles, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { ICarrierApplicationTruckNumber, IP44TruckNumberStates, Maybe } from 'types/graphqlTypes'
import { useSearchCarrierApplicationTruckNumbersQuery } from '../graphql/SearchCarrierApplicationTruckNumbers'

export interface ITruckNumberInputProps {
  truckNumber: Maybe<string>
  carrierApplicationId: string
}

interface ICheckIfTruckEledConnectedProps
  extends Pick<ICarrierApplicationTruckNumber, 'keepTruckinId' | 'p44State'> {}

type TTruck = Pick<ICarrierApplicationTruckNumber, 'keepTruckinId' | 'p44State' | 'id' | 'value'>

const checkTruckEldConnectedType = ({
  keepTruckinId,
  p44State,
}: ICheckIfTruckEledConnectedProps) => {
  const p44Enabled = p44State === 'valid'
  if (keepTruckinId) {
    if (p44Enabled) return 'KT & P44'
    return 'KT'
  }
  if (p44State === 'valid') return 'P44'
  return null
}

const useTruckNumberInputStyles = makeStyles(() =>
  createStyles({
    inputDescription: {
      color: brandColors.info,
    },
  })
)

interface IGetComputedTrucksProps extends Pick<ITruckNumberInputProps, 'truckNumber'> {
  trucks: TTruck[]
}

const getComputedTrucks = ({ truckNumber, trucks }: IGetComputedTrucksProps): TTruck[] => {
  const hasTruckNumberInstance = trucks.find(({ value }) => value === truckNumber)

  if (hasTruckNumberInstance || !truckNumber) {
    return trucks
  }

  return [
    {
      id: `-${truckNumber}`,
      keepTruckinId: null,
      p44State: IP44TruckNumberStates.invalid,
      value: truckNumber || '',
    },
    ...trucks,
  ]
}

interface ITruckNumberInputValues {
  className: string
  trackingMethod?: string
}

const truckToAutocompleteBaseOption = (
  { id = '', keepTruckinId, p44State, value }: TTruck,
  { className = 'inputDescription' }: ITruckNumberInputValues
): IAutocompleteBaseOption => {
  const eldConnected = checkTruckEldConnectedType({
    p44State,
    keepTruckinId,
  })
  return {
    id,
    text: value,
    groupIndex: 1,
    iconName: 'FlatBedIcon',
    descriptionClassName: className,
    description: eldConnected ? `(${eldConnected} connected)` : '',
  }
}

export interface ITruckAutocompleteProps extends ControllerRenderProps {
  carrierApplicationId: string
  truckNumber?: string
  trackingMethod?: string
  AutocompleteProps?: Omit<
    React.ComponentProps<typeof AutocompleteBase>,
    'options' | 'defaultValue' | 'onChange' | 'value'
  >
  dataTest?: string
}

export const TruckAutocomplete: FC<ITruckAutocompleteProps> = ({
  AutocompleteProps = {},
  carrierApplicationId,
  onChange,
  truckNumber,
  value,
  dataTest,
}) => {
  const styles = useTruckNumberInputStyles()
  const { options = [] } = useAutocompleteQuery(
    useSearchCarrierApplicationTruckNumbersQuery,
    {
      variables: { carrierApplicationId, searchTerm: '' },
    },
    {
      getOptionsConfig: data => {
        const { searchAllCarrierApplicationTruckNumbers: result } = data || {}
        const { edges } = result || {}
        const trucks = compact(edges?.map(edge => edge?.node))

        const computedTrucks = getComputedTrucks({
          trucks,
          truckNumber,
        })

        return {
          options: computedTrucks,
          groupIndex: 1,
          optionType: 'withIconAndDescription',
          mapOption: truck =>
            truckToAutocompleteBaseOption(truck, { className: styles.inputDescription }),
        }
      },
    }
  )
  const inputHandler: TInputChangeHandler = (event, selectedItem) => {
    if (event !== null) onChange(selectedItem)
  }
  const optionSelectedHandler = ({ text }: IAutocompleteBaseOption) => text === value

  return (
    <AutocompleteBase
      {...AutocompleteProps}
      clearOnBlur={false}
      autoSelect={false}
      options={options}
      data-test={dataTest}
      onInputChange={inputHandler}
      isOptionEqualToValue={optionSelectedHandler}
      inputValue={value}
    />
  )
}
